import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPasswordProtection } from '@/store/reducers/passwordProtectionSlice';
import { selectPasswordProtection } from '@/store/selectors/password-protection.selector';
import {
  extensionUtils,
  encodePassword,
  logger,
  randomPassword,
} from '@/utils';
import { apiUser } from '@/utils/api/apiUser';
import { EXTENSION_MESSAGE_TYPE } from '@/models/extension.model';
import { LOGGER_ACTION, LOGGER_FEATURE } from '@/constants/logger/logger-tags';

const RESET_PASSWORD_LENGTH = 5;
const FALLBACK_PASSWORD = '11jdksdo23araf';
const FALLBACK_ENCODED_PASSWORD = 'MTFqZGtzZG8yM2FyYWY=';

/**
 * Generates a new password, sends it to the extension (which emails it to the user),
 * stores its encoded form locally and saves it to the server so every page and device
 * validates against the new password.
 */
export const usePasswordReset = (): (() => Promise<void>) => {
  const dispatch = useDispatch();
  const passwordProtection = useSelector(selectPasswordProtection);

  const saveToServer = useCallback(
    async (encodedPassword: string): Promise<void> => {
      try {
        await apiUser.saveUserConfig({
          passwordProtection: { ...passwordProtection, pass: encodedPassword },
        });
      } catch (error) {
        logger.error('Failed to save reset password to server', error, {
          tags: {
            feature: LOGGER_FEATURE.USER,
            action: LOGGER_ACTION.SAVE_USER_CONFIG,
          },
        });
      }
    },
    [passwordProtection]
  );

  return useCallback(async (): Promise<void> => {
    let newPassword = randomPassword(RESET_PASSWORD_LENGTH);
    let encodedPassword = newPassword ? encodePassword(newPassword) : null;

    if (!newPassword || !encodedPassword) {
      newPassword = FALLBACK_PASSWORD;
      encodedPassword = FALLBACK_ENCODED_PASSWORD;
    }

    extensionUtils
      .sendMessageToExtension(
        EXTENSION_MESSAGE_TYPE.RESET_PASSWORD_PROTECTION_PASS,
        { newPassword, encodedPassword }
      )
      .finally();

    dispatch(setPasswordProtection({ pass: encodedPassword }));
    await saveToServer(encodedPassword);
  }, [dispatch, saveToServer]);
};
